import React, { createContext, useContext, ReactNode, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShabtaiCalculator } from '@/features/calculator/hooks/useShabtaiCalculator';
import { CalculationInput, CalculationResult } from '@/types';
import { useAuth } from '@/features/auth/hooks/useAuth';

import { checkForDuplicateMap } from '@/features/maps/core/mapQueries';
import { normalizeText, normalizeDate } from '@/features/maps/core/mapTransforms';
import { formatForDisplay } from '@/utils/dates';
import { dbFetchMapById } from '@/api/maps';
import { sampleMaps } from '@/features/maps/core/sampleData';
import { addRecentCalculatedMap, getStoredRecentMaps } from '@/features/maps/core/useRecentCalculatedMaps';
import { getCachedSavedMaps } from '@/features/maps/core/useMaps';

interface CalculatorContextType {
    input: CalculationInput;
    setInput: (input: CalculationInput | ((prev: CalculationInput) => CalculationInput)) => void;
    result: CalculationResult;
    isCalculating: boolean;
    isMapLoading: boolean;
    showResults: boolean;
    setShowResults: (show: boolean) => void;
    resetCalculator: () => void;
    isMyMapCreationMode: boolean;
    setIsMyMapCreationMode: (isMyMap: boolean) => void;
    currentMapId: string | undefined;
    setCurrentMapId: (id: string | undefined) => void;
    isSaveDialogOpen: boolean;
    setIsSaveDialogOpen: (isOpen: boolean) => void;
    originalMapRef: React.MutableRefObject<CalculationInput | null>;
    loadedMapRef: React.MutableRefObject<CalculationInput | null>;
}

const CalculatorContext = createContext<CalculatorContextType | undefined>(undefined);

export const CalculatorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { input, setInput, result, isCalculating } = useShabtaiCalculator();
    const [searchParams, setSearchParams] = useSearchParams();

    // Sync showResults with URL ?results=true
    // showResults is now a local state to allow "Consume & Clear" behavior
    const [showResults, setShowResultsState] = React.useState(() => searchParams.get('results') === 'true');
    const [isMapLoading, setIsMapLoading] = React.useState(false);

    const setShowResults = useCallback((show: boolean) => {
        setShowResultsState(show);
        const newParams = new URLSearchParams(searchParams);
        if (show) {
            newParams.set('results', 'true');
        } else {
            newParams.delete('results');
        }
        setSearchParams(newParams, { replace: true });
    }, [searchParams, setSearchParams]);

    // Sync currentMapId with URL ?id=uuid
    const currentMapId = searchParams.get('id') || undefined;
    const setCurrentMapId = useCallback((id: string | undefined) => {
        const newParams = new URLSearchParams(searchParams);
        if (id) {
            newParams.set('id', id);
        } else {
            newParams.delete('id');
        }
        setSearchParams(newParams, { replace: true });
    }, [searchParams, setSearchParams]);

    // Local-only UI states
    const [isMyMapCreationMode, setIsMyMapCreationMode] = React.useState(false);
    const [isSaveDialogOpen, setIsSaveDialogOpen] = React.useState(false);

    const { user, loading } = useAuth();
    const wasLoggedIn = useRef<boolean>(false);
    const loadedMapIdRef = useRef<string | undefined>(undefined);
    const loadedMapRef = useRef<CalculationInput | null>(null);
    const originalMapIdRef = useRef<string | undefined>(undefined);
    const originalMapRef = useRef<CalculationInput | null>(null);
    const decoupledMapRef = useRef<string | undefined>(undefined);

    // Unified URL Hydration Engine: Handles ?id=uuid, sample maps, and direct query parameters
    useEffect(() => {
        if (loading) return;

        // --- PATH A: Direct Input Parameters in URL (e.g. from Recent Maps or external links) ---
        const urlFirstName = searchParams.get('firstName') || searchParams.get('fn');
        const urlBirthDate = searchParams.get('birthDate') || searchParams.get('bd');
        const urlSunLongitude = searchParams.get('sunLongitude');
        const urlSoulRoot = searchParams.get('soulRoot');

        if (urlFirstName && urlBirthDate) {
            const urlInput: CalculationInput = {
                firstName: urlFirstName || '',
                familyName: searchParams.get('familyName') || searchParams.get('ln') || '',
                motherName: searchParams.get('motherName') || searchParams.get('mn') || '',
                birthDate: formatForDisplay(urlBirthDate),
                birthTime: searchParams.get('birthTime') || '',
                lat: searchParams.get('lat') ? Number(searchParams.get('lat')) : undefined,
                lng: searchParams.get('lng') ? Number(searchParams.get('lng')) : undefined,
                cityName: searchParams.get('cityName') || '',
                timezone: searchParams.get('timezone') || undefined,
                gender: (searchParams.get('gender') as CalculationInput['gender']) || undefined,
                sunLongitude: urlSunLongitude ? Number(urlSunLongitude) : undefined,
                soul_root: urlSoulRoot || undefined,
                isLocationDetected: Boolean(searchParams.get('lat') && searchParams.get('lng'))
            };

            loadedMapIdRef.current = currentMapId;
            loadedMapRef.current = urlInput;
            originalMapIdRef.current = currentMapId;
            originalMapRef.current = urlInput;

            setTimeout(() => {
                setInput({
                    ...urlInput,
                    isProgrammatic: true
                });

                if (searchParams.get('results') === 'true') {
                    setShowResultsState(true);
                }
                setIsMapLoading(false);
            }, 0);
            return;
        }

        // If no ID and no URL parameters, reset refs
        if (!currentMapId) {
            loadedMapIdRef.current = undefined;
            loadedMapRef.current = null;
            setTimeout(() => setIsMapLoading(false), 0);
            return;
        }

        // --- PATH B: Loading by ID (?id=uuid or sample map) ---
        if (currentMapId !== decoupledMapRef.current) {
            decoupledMapRef.current = undefined;
        }

        // 1. Sample Map Check (available to everyone)
        const sample = sampleMaps.find(m => m.id === currentMapId);
        if (sample) {
            loadedMapIdRef.current = currentMapId;
            let sampleCityName = 'תל אביב';
            if (sample.id === 'sample-2') sampleCityName = 'ירושלים';
            if (sample.id === 'sample-3') sampleCityName = 'חיפה';

            const baseInput: CalculationInput = {
                firstName: sample.firstName || '',
                familyName: sample.familyName || '',
                motherName: sample.motherName || '',
                birthDate: formatForDisplay(sample.birthDate),
                birthTime: sample.birth_time || '',
                lat: sample.birth_lat === null ? undefined : sample.birth_lat,
                lng: sample.birth_lon === null ? undefined : sample.birth_lon,
                cityName: sampleCityName,
                timezone: sample.timezone || undefined,
                gender: sample.gender || undefined,
                soul_root: sample.soul_root || undefined,
                isLocationDetected: Boolean(sample.birth_lat && sample.birth_lon)
            };

            originalMapIdRef.current = currentMapId;
            originalMapRef.current = baseInput;
            loadedMapRef.current = baseInput;

            setTimeout(() => {
                setInput({
                    ...baseInput,
                    isProgrammatic: true
                });

                if (searchParams.get('results') === 'true') {
                    setShowResultsState(true);
                }
                setIsMapLoading(false);
            }, 0);
            return;
        }

        // 2. Local DB Snapshot Cache Instant Hydration (0ms Turn-0)
        const cachedSavedMap = getCachedSavedMaps(user?.id)?.find(m => m.id === currentMapId);
        const recentStored = getStoredRecentMaps().find(m => m.id === currentMapId);
        const memoryMatch = cachedSavedMap || recentStored;

        if (memoryMatch) {
            const astroSunLon = (cachedSavedMap?.astrology_cache as { planets?: { sun?: { longitude?: number } } } | undefined)?.planets?.sun?.longitude ?? recentStored?.sunLongitude;

            const savedInner = cachedSavedMap?.inner_sign_number ? { number: cachedSavedMap.inner_sign_number, sign: cachedSavedMap.inner_sign_name || undefined } : undefined;
            const savedSurrounding = cachedSavedMap?.surrounding_sign_number ? { number: cachedSavedMap.surrounding_sign_number, sign: cachedSavedMap.surrounding_sign_name || undefined, gematria: cachedSavedMap.gematria_value === null ? undefined : cachedSavedMap.gematria_value } : undefined;
            const savedTur = cachedSavedMap?.tur_value ? { value: cachedSavedMap.tur_value, label: cachedSavedMap.tur_label || '' } : undefined;
            const savedProfile = cachedSavedMap?.profile_number ? { profileNumber: cachedSavedMap.profile_number, profileSign: cachedSavedMap.profile_sign || '' } : undefined;

            const cachedInput: CalculationInput = {
                firstName: memoryMatch.firstName || '',
                familyName: memoryMatch.familyName || '',
                motherName: memoryMatch.motherName || '',
                birthDate: formatForDisplay(memoryMatch.birthDate) || '',
                birthTime: ('birth_time' in memoryMatch ? memoryMatch.birth_time : '') || '',
                lat: 'birth_lat' in memoryMatch ? (memoryMatch.birth_lat === null ? undefined : memoryMatch.birth_lat) : undefined,
                lng: 'birth_lon' in memoryMatch ? (memoryMatch.birth_lon === null ? undefined : memoryMatch.birth_lon) : undefined,
                cityName: ('birth_location' in memoryMatch ? memoryMatch.birth_location : ('city_name' in memoryMatch ? memoryMatch.city_name : '')) || '',
                timezone: memoryMatch.timezone || undefined,
                gender: (memoryMatch.gender as CalculationInput['gender']) || undefined,
                sunLongitude: astroSunLon !== undefined ? astroSunLon : undefined,
                soul_root: ('soul_root' in memoryMatch ? memoryMatch.soul_root : undefined) || undefined,
                savedInnerSign: savedInner,
                savedSurroundingSign: savedSurrounding,
                savedTur: savedTur,
                savedProfile: savedProfile,
                isLocationDetected: Boolean('birth_lat' in memoryMatch && memoryMatch.birth_lat && memoryMatch.birth_lon)
            };

            loadedMapIdRef.current = currentMapId;
            originalMapIdRef.current = currentMapId;
            originalMapRef.current = cachedInput;
            loadedMapRef.current = cachedInput;

            setTimeout(() => {
                setInput({
                    ...cachedInput,
                    isProgrammatic: true
                });

                if (searchParams.get('results') === 'true') {
                    setShowResultsState(true);
                }
                setIsMapLoading(false);
            }, 0);
        }

        // 3. Remote Database Fetch for Authenticated Users (Background SSOT Validation)
        if (!user) {
            setTimeout(() => setIsMapLoading(false), 0);
            return;
        }

        let isMounted = true;
        const fetchRealMap = async () => {
            if (!memoryMatch) {
                setIsMapLoading(true);
            }
            try {
                const mapData = await dbFetchMapById(currentMapId);
                if (!isMounted || !mapData) {
                    if (isMounted) setIsMapLoading(false);
                    return;
                }

                loadedMapIdRef.current = currentMapId;
                const dbSunLon = (mapData.astrology_cache as { planets?: { sun?: { longitude?: number } } } | undefined)?.planets?.sun?.longitude;

                const savedInner = mapData.inner_sign_number ? { number: mapData.inner_sign_number, sign: mapData.inner_sign_name || undefined } : undefined;
                const savedSurrounding = mapData.surrounding_sign_number ? { number: mapData.surrounding_sign_number, sign: mapData.surrounding_sign_name || undefined, gematria: mapData.gematria_value === null ? undefined : mapData.gematria_value } : undefined;
                const savedTur = mapData.tur_value ? { value: mapData.tur_value, label: mapData.tur_label || '' } : undefined;
                const savedProfile = mapData.profile_number ? { profileNumber: mapData.profile_number, profileSign: mapData.profile_sign || '' } : undefined;

                const baseInput: CalculationInput = {
                    firstName: mapData.firstName || '',
                    familyName: mapData.familyName || '',
                    motherName: mapData.motherName || '',
                    birthDate: formatForDisplay(mapData.birthDate),
                    birthTime: mapData.birth_time || '',
                    lat: mapData.birth_lat === null ? undefined : mapData.birth_lat,
                    lng: mapData.birth_lon === null ? undefined : mapData.birth_lon,
                    cityName: mapData.birth_location || '',
                    timezone: mapData.timezone || undefined,
                    gender: mapData.gender || undefined,
                    sunLongitude: dbSunLon !== undefined ? dbSunLon : undefined,
                    soul_root: mapData.soul_root || undefined,
                    savedInnerSign: savedInner,
                    savedSurroundingSign: savedSurrounding,
                    savedTur: savedTur,
                    savedProfile: savedProfile,
                    isLocationDetected: Boolean(mapData.birth_lat && mapData.birth_lon)
                };

                originalMapIdRef.current = currentMapId;
                originalMapRef.current = baseInput;
                loadedMapRef.current = baseInput;

                setTimeout(() => {
                    if (!isMounted) return;
                    setInput({
                        ...baseInput,
                        isProgrammatic: true
                    });

                    if (searchParams.get('results') === 'true') {
                        setShowResultsState(true);
                    }
                    setIsMapLoading(false);
                }, 0);
            } catch (err) {
                console.error("Failed to load map by ID from database:", err);
                if (isMounted) setIsMapLoading(false);
            }
        };

        fetchRealMap();

        return () => {
            isMounted = false;
        };
    }, [currentMapId, user, loading, searchParams, setInput]);

    const resetCalculator = useCallback(() => {
        setInput({
            firstName: '',
            familyName: '',
            motherName: '',
            birthDate: '',
            birthTime: '',
            lat: undefined,
            lng: undefined,
            cityName: '',
            timezone: undefined
        });

        // Reset URL params
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('results');
        newParams.delete('id');
        newParams.delete('birthTime');
        newParams.delete('lat');
        newParams.delete('lng');
        newParams.delete('cityName');
        newParams.delete('timezone');
        setSearchParams(newParams, { replace: true });

        setIsMyMapCreationMode(false);
        setIsSaveDialogOpen(false);
        setShowResultsState(false);
        originalMapIdRef.current = undefined;
        originalMapRef.current = null;
    }, [setInput, searchParams, setSearchParams]);

    // Handle logout cleanup
    useEffect(() => {
        if (!loading) {
            // Transitions from logged in (true) to logged out (false)
            if (wasLoggedIn.current && !user) {
                // Defer reset to next tick to avoid cascading renders warning
                setTimeout(() => resetCalculator(), 0);
            }
            // Sync current state for next transition
            wasLoggedIn.current = !!user;
        }
    }, [user, loading, resetCalculator]);

    // T-113: Guest Keystroke Capturing (Auto-save draft when guest types)
    useEffect(() => {
        if (loading || user) return;

        // Check if there is actual input worth saving (not completely empty)
        const hasMinimalData = input.firstName?.trim() ||
                              input.familyName?.trim() ||
                              input.motherName?.trim() ||
                              (input.birthDate && input.birthDate !== '');

        if (hasMinimalData) {
            localStorage.setItem('spirit_calculator_draft', JSON.stringify(input));
        }
    }, [input, user, loading]);

    // T-113: Draft Restoration on successful sign-in
    useEffect(() => {
        if (loading || !user) return;

        const savedDraft = localStorage.getItem('spirit_calculator_draft');
        if (savedDraft) {
            try {
                const parsedDraft = JSON.parse(savedDraft) as CalculationInput;
                
                // Defer state updates to next event loop tick to prevent synchronous cascading renders warning
                setTimeout(() => {
                    // Use programmatic flag to prevent auto-decoupling from matching maps
                    setInput({
                        ...parsedDraft,
                        isProgrammatic: true
                    });

                    // Automatically display the results page
                    setShowResults(true);
                }, 0);

                // Clear the draft from memory
                localStorage.removeItem('spirit_calculator_draft');
            } catch (err) {
                console.error("Failed to restore guest calculator draft:", err);
            }
        }
    }, [user, loading, setInput, setShowResults]);

    // Automatic Map ID Resolution & Matching
    useEffect(() => {
        if (loading || !user || currentMapId) return;

        // Ensure we have minimal viable input before querying duplicates
        const first = input.firstName?.trim();
        const birth = input.birthDate?.trim();
        if (!first || !birth) return;

        let isMounted = true;

        const performMapMatch = async () => {
            try {
                const normalizedFirst = normalizeText(first)!;
                const normalizedFamily = normalizeText(input.familyName);
                const normalizedMother = normalizeText(input.motherName);
                const normalizedBirth = normalizeDate(birth);

                if (!normalizedFirst || !normalizedBirth) return;

                const matchingId = await checkForDuplicateMap(
                    user.id,
                    normalizedFirst,
                    normalizedFamily,
                    normalizedMother,
                    normalizedBirth
                );

                if (isMounted && matchingId) {
                    if (decoupledMapRef.current === matchingId) {
                        return;
                    }
                    setCurrentMapId(matchingId);
                }
            } catch (err) {
                console.error("Failed to auto-resolve duplicate map ID:", err);
            }
        };

        // Debounce map resolution to avoid excessive queries during typing
        const delay = setTimeout(performMapMatch, 600);
        return () => {
            isMounted = false;
            clearTimeout(delay);
        };
    }, [input, user, loading, currentMapId, setCurrentMapId]);

    // Auto-capture calculated map into recent calculated maps history
    useEffect(() => {
        if (showResults && input.firstName && input.birthDate) {
            addRecentCalculatedMap({
                id: currentMapId,
                firstName: input.firstName,
                familyName: input.familyName,
                motherName: input.motherName,
                birthDate: input.birthDate,
                birth_time: input.birthTime,
                birth_lat: input.lat,
                birth_lon: input.lng,
                city_name: input.cityName,
                gender: input.gender,
                timezone: input.timezone,
                sunLongitude: input.sunLongitude,
                soul_root: input.soul_root
            });
        }
    }, [showResults, input.firstName, input.familyName, input.motherName, input.birthDate, input.birthTime, input.lat, input.lng, input.cityName, input.gender, input.timezone, input.sunLongitude, input.soul_root, currentMapId]);

    // Wrapped input setter callback to intercept user modifications, 
    // handle auto-reset of time and place on birth date change, 
    // and auto-decouple currentMapId if inputs drift from originally loaded map state.
    const wrappedSetInput = useCallback((value: CalculationInput | ((prev: CalculationInput) => CalculationInput)) => {
        setInput(prev => {
            let next = typeof value === 'function' ? value(prev) : value;

            // Extract isProgrammatic flag
            const isProgrammatic = next.isProgrammatic;
            if (isProgrammatic) {
                // Strip the programmatic flag from final state
                const { isProgrammatic: _, ...cleanInput } = next;
                next = cleanInput as CalculationInput;

                // Sync loadedMapRef immediately so decoupling isn't triggered
                loadedMapRef.current = next;
                return next;
            }

            // 1. Decoupling & Auto-Reset on Date Change logic
            const isDateChanged = next.birthDate !== prev.birthDate;
            if (isDateChanged) {
                // Auto-reset time and place counters/parameters
                next = {
                    ...next,
                    birthTime: '',
                    cityName: '',
                    lat: undefined,
                    lng: undefined,
                    timezone: undefined,
                    isLocationDetected: false
                };
            }

            // Reset decoupledMapRef, clear saved DB overrides, and clear URL id if identifying fields change
            const isIdentityChanged = next.firstName !== prev.firstName ||
                                       next.familyName !== prev.familyName ||
                                       next.motherName !== prev.motherName ||
                                       next.birthDate !== prev.birthDate;
            if (isIdentityChanged) {
                decoupledMapRef.current = undefined;
                next = {
                    ...next,
                    savedInnerSign: undefined,
                    savedSurroundingSign: undefined,
                    savedTur: undefined,
                    savedProfile: undefined,
                };
                if (currentMapId) {
                    setTimeout(() => {
                        const newParams = new URLSearchParams(window.location.search);
                        if (newParams.has('id')) {
                            newParams.delete('id');
                            setSearchParams(newParams, { replace: true });
                        }
                    }, 0);
                }
            }

            // 2. Set URL overrides if parameters drift from originally loaded map
            if (currentMapId && originalMapRef.current) {
                const isTimeDiff = next.birthTime !== originalMapRef.current.birthTime;
                const isLatDiff = next.lat !== originalMapRef.current.lat;
                const isLngDiff = next.lng !== originalMapRef.current.lng;
                const isCityDiff = next.cityName !== originalMapRef.current.cityName;
                const isTzDiff = next.timezone !== originalMapRef.current.timezone;

                // Keep map coupled, but update URL overrides for simulation parameters
                setTimeout(() => {
                    const newParams = new URLSearchParams(window.location.search);
                    let hasChanges = false;

                    if (isTimeDiff) {
                        newParams.set('birthTime', next.birthTime || '');
                        hasChanges = true;
                    } else {
                        if (newParams.has('birthTime')) {
                            newParams.delete('birthTime');
                            hasChanges = true;
                        }
                    }

                    if (isLatDiff) {
                        newParams.set('lat', next.lat !== undefined ? String(next.lat) : '');
                        hasChanges = true;
                    } else {
                        if (newParams.has('lat')) {
                            newParams.delete('lat');
                            hasChanges = true;
                        }
                    }

                    if (isLngDiff) {
                        newParams.set('lng', next.lng !== undefined ? String(next.lng) : '');
                        hasChanges = true;
                    } else {
                        if (newParams.has('lng')) {
                            newParams.delete('lng');
                            hasChanges = true;
                        }
                    }

                    if (isCityDiff) {
                        newParams.set('cityName', next.cityName || '');
                        hasChanges = true;
                    } else {
                        if (newParams.has('cityName')) {
                            newParams.delete('cityName');
                            hasChanges = true;
                        }
                    }

                    if (isTzDiff) {
                        newParams.set('timezone', next.timezone !== undefined ? String(next.timezone) : '');
                        hasChanges = true;
                    } else {
                        if (newParams.has('timezone')) {
                            newParams.delete('timezone');
                            hasChanges = true;
                        }
                    }

                    if (hasChanges) {
                        setSearchParams(newParams, { replace: true });
                    }
                }, 0);
            }

            // 3. Re-couple to original map if inputs match original inputs exactly
            if (!currentMapId && originalMapIdRef.current && originalMapRef.current) {
                const isFirstNameMatch = next.firstName === originalMapRef.current.firstName;
                const isFamilyNameMatch = next.familyName === originalMapRef.current.familyName;
                const isMotherNameMatch = next.motherName === originalMapRef.current.motherName;
                const isDateMatch = next.birthDate === originalMapRef.current.birthDate;
                const isTimeMatch = next.birthTime === originalMapRef.current.birthTime;
                const isLatMatch = next.lat === originalMapRef.current.lat;
                const isLngMatch = next.lng === originalMapRef.current.lng;
                const isTzMatch = next.timezone === originalMapRef.current.timezone;

                if (isFirstNameMatch && isFamilyNameMatch && isMotherNameMatch && isDateMatch && isTimeMatch && isLatMatch && isLngMatch && isTzMatch) {
                    decoupledMapRef.current = undefined;
                    const origId = originalMapIdRef.current;
                    const origInput = originalMapRef.current;
                    setTimeout(() => {
                        setCurrentMapId(origId);
                        loadedMapIdRef.current = origId;
                        loadedMapRef.current = origInput;
                    }, 0);
                }
            }

            return next;
        });
    }, [currentMapId, setInput, setCurrentMapId, setSearchParams]);

    const value = useMemo(() => ({
        input,
        setInput: wrappedSetInput,
        result,
        isCalculating,
        isMapLoading,
        showResults,
        setShowResults,
        resetCalculator,
        isMyMapCreationMode,
        setIsMyMapCreationMode,
        currentMapId,
        setCurrentMapId,
        isSaveDialogOpen,
        setIsSaveDialogOpen,
        originalMapRef,
        loadedMapRef
    }), [
        input, wrappedSetInput, result, isCalculating, isMapLoading,
        showResults, setShowResults, resetCalculator,
        isMyMapCreationMode, currentMapId, setCurrentMapId,
        isSaveDialogOpen, originalMapRef, loadedMapRef
    ]);

    return (
        <CalculatorContext.Provider value={value}>
            {children}
        </CalculatorContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCalculator = () => {
    const context = useContext(CalculatorContext);
    if (context === undefined) {
        throw new Error('useCalculator must be used within a CalculatorProvider');
    }
    return context;
};
